import type { Persona } from '@/services/personaService';
import type { PlanTier } from '@/hooks/useSubscription';

export interface PersonaLockStatus {
    isLocked: boolean;
    hoursRemaining: number;
    canEditDemographics: boolean;
    canEditArchetype: boolean;
    canEditBehavior: boolean;
}

/**
 * Avalia se a persona está bloqueada para alterações estruturais no plano Basic.
 * Regra: No plano Basic, após 24 horas da criação, os dados estruturais e arquetípicos
 * tornam-se somente-leitura para evitar reciclagem de personas e burla de limites.
 */
export function isPersonaLocked(
    persona?: Persona | null,
    tier: PlanTier = 'basic',
    isSpecialGrant: boolean = false
): PersonaLockStatus {
    // Planos Pro, Business, VIP ou nova persona nunca são bloqueados
    if (!persona || isSpecialGrant || (tier !== 'basic' && tier !== 'trial')) {
        return {
            isLocked: false,
            hoursRemaining: 0,
            canEditDemographics: true,
            canEditArchetype: true,
            canEditBehavior: true,
        };
    }

    // Se não houver data de criação registrada, assume a data atual
    const createdAt = persona.createdAt ? new Date(persona.createdAt).getTime() : Date.now();
    const now = Date.now();
    const elapsedMs = Math.max(0, now - createdAt);
    const gracePeriodMs = 24 * 60 * 60 * 1000; // 24 horas

    if (elapsedMs < gracePeriodMs) {
        const hoursRemaining = Math.max(1, Math.ceil((gracePeriodMs - elapsedMs) / (1000 * 60 * 60)));
        return {
            isLocked: false,
            hoursRemaining,
            canEditDemographics: true,
            canEditArchetype: true,
            canEditBehavior: true,
        };
    }

    // Após 24h no plano Basic:
    return {
        isLocked: true,
        hoursRemaining: 0,
        canEditDemographics: false,
        canEditArchetype: false,
        canEditBehavior: true, // Dores e gatilhos podem ser refinados com moderação
    };
}

/**
 * Calcula o desvio / distância percentual entre dois textos (0% = idênticos, 100% = completamente diferentes).
 * Utiliza coeficiente de Jaccard normalizado por conjuntos de palavras e tamanho relativo.
 */
export function calculateTextDrift(originalText: string = '', newText: string = ''): number {
    const origClean = originalText.trim().toLowerCase();
    const newClean = newText.trim().toLowerCase();

    if (origClean === newClean) return 0;
    if (!origClean && !newClean) return 0;
    if (!origClean || !newClean) return 100;

    const tokenize = (text: string): Set<string> => {
        const words = text
            .replace(/[^\w\sáéíóúâêîôûãõç]/gi, ' ')
            .split(/\s+/)
            .filter(w => w.length > 1);
        return new Set(words);
    };

    const setOrig = tokenize(origClean);
    const setNew = tokenize(newClean);

    if (setOrig.size === 0 && setNew.size === 0) return 0;
    if (setOrig.size === 0 || setNew.size === 0) return 100;

    const intersection = new Set([...setOrig].filter(x => setNew.has(x)));
    const union = new Set([...setOrig, ...setNew]);

    const jaccardSimilarity = intersection.size / union.size;
    const driftPercent = Math.round((1 - jaccardSimilarity) * 100);

    return Math.min(100, Math.max(0, driftPercent));
}

export interface ValidationResult {
    valid: boolean;
    reason?: string;
    field?: string;
}

/**
 * Valida se uma atualização de persona respeita as diretrizes de segurança anti-abuso.
 */
export function validatePersonaUpdate(
    original: Persona,
    updated: Partial<Persona>,
    tier: PlanTier = 'basic',
    isSpecialGrant: boolean = false
): ValidationResult {
    // Se for plano Pro, Business ou VIP, qualquer edição é 100% permitida
    if (isSpecialGrant || (tier !== 'basic' && tier !== 'trial')) {
        return { valid: true };
    }

    const lockStatus = isPersonaLocked(original, tier, isSpecialGrant);

    // Se ainda está na janela de 24h de tolerância, permite refinamentos
    if (!lockStatus.isLocked) {
        return { valid: true };
    }

    // 1. Validação de Dados Demográficos Estruturais (100% fixados após 24h no Basic)
    if (updated.name !== undefined && updated.name.trim() !== original.name.trim()) {
        return {
            valid: false,
            field: 'name',
            reason: 'No plano Basic, o nome da persona não pode ser alterado após 24h da criação. Faça upgrade para o Pro para edições ilimitadas.',
        };
    }

    if (updated.occupation !== undefined && updated.occupation.trim() !== original.occupation.trim()) {
        return {
            valid: false,
            field: 'occupation',
            reason: 'No plano Basic, a ocupação/profissão da persona não pode ser alterada após 24h da criação.',
        };
    }

    if (updated.age !== undefined && String(updated.age).trim() !== String(original.age).trim()) {
        return {
            valid: false,
            field: 'age',
            reason: 'No plano Basic, a idade da persona não pode ser alterada após 24h da criação.',
        };
    }

    if (updated.gender !== undefined && updated.gender.trim() !== original.gender.trim()) {
        return {
            valid: false,
            field: 'gender',
            reason: 'No plano Basic, o gênero da persona não pode ser alterado após 24h da criação.',
        };
    }

    if (updated.location !== undefined && updated.location.trim() !== original.location.trim()) {
        return {
            valid: false,
            field: 'location',
            reason: 'No plano Basic, a localização da persona não pode ser alterada após 24h da criação.',
        };
    }

    if (updated.income !== undefined && updated.income.trim() !== original.income.trim()) {
        return {
            valid: false,
            field: 'income',
            reason: 'No plano Basic, a renda da persona não pode ser alterada após 24h da criação.',
        };
    }

    // 2. Validação do Núcleo Arquetípico (Bio e Visão de Mundo fixadas após 24h)
    if (updated.bio !== undefined && updated.bio.trim() !== original.bio.trim()) {
        return {
            valid: false,
            field: 'bio',
            reason: 'No plano Basic, a biografia da persona é protegida após 24h para manter a integridade dos testes.',
        };
    }

    if (updated.worldview !== undefined && updated.worldview.trim() !== original.worldview.trim()) {
        return {
            valid: false,
            field: 'worldview',
            reason: 'No plano Basic, a visão de mundo da persona é protegida após 24h para manter a integridade dos testes.',
        };
    }

    // 3. Validação de Integridade Comportamental (Dores e Gatilhos não podem ser 100% substituídos)
    if (updated.painPoints !== undefined && original.painPoints) {
        const drift = calculateTextDrift(original.painPoints, updated.painPoints);
        if (drift > 45) {
            return {
                valid: false,
                field: 'painPoints',
                reason: 'Detectada alteração drástica nas dores principais (> 45% de desvio). No plano Basic, para simular um nicho ou dor totalmente nova, crie uma nova persona ou faça upgrade para o Pro.',
            };
        }
    }

    if (updated.triggers !== undefined && original.triggers) {
        const drift = calculateTextDrift(original.triggers, updated.triggers);
        if (drift > 45) {
            return {
                valid: false,
                field: 'triggers',
                reason: 'Detectada alteração drástica nos gatilhos da persona. No plano Basic, edições radicais exigem o Plano Pro.',
            };
        }
    }

    return { valid: true };
}
